import React, {
  useRef, useState, useEffect, useCallback, useMemo,
} from 'react';
import Hls from 'hls.js';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Icon } from '@iconify/react';
import { Link, Image } from '../common/NextAdapters';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { useAuthStore, useWatchStore } from '../../store';
import { homeApi, payoutApi } from '../../api/client';
import { getApiErrorMessage } from '../../api/errors';
import { getDeviceFingerprint } from '../../utils/deviceId';
import { savePendingHeartbeat } from '../../utils/pendingHeartbeat';
import { buildSeasons, flattenSeasons, formatEpisodeDuration } from '../../lib/seasons';
import { deriveThumbnailFromVideoUrl } from '../../lib/video-helper';
import {
  useEpisodeDetail, useComments, useReactToEpisode, usePostComment,
} from '../../hooks/useMovie';

dayjs.extend(relativeTime);

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];

function formatTime(seconds) {
  if (!seconds || !isFinite(seconds) || isNaN(seconds)) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Full-screen cinema player (design ported from kast_next_designs' VideoPlayerView):
 * custom controls, HLS adaptive bitrate, quality/audio/subtitle switching, episodes drawer,
 * next-episode autoplay prompt, keyboard shortcuts, and an anti-screen-recording privacy blur —
 * layered on top of this app's existing payout heartbeat / watch-progress engine and
 * likes+comments, neither of which exist in the reference design.
 */
export function CinemaPlayer({ title, initialEpisodeId }) {
  const { token, user } = useAuthStore();
  const isAuthenticated = !!token;
  const isAdmin = user?.Role === 'Admin';
  const { saveProgress, getProgress } = useWatchStore();

  const playerContainerRef = useRef(null);
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const hideTimerRef = useRef(null);
  const hlsRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Series / Episode navigation. Episodes run season by season, so "next episode" carries
  // straight on from a season's last episode into the next season.
  const seasons = useMemo(() => buildSeasons(title), [title]);
  const episodes = useMemo(() => flattenSeasons(seasons), [seasons]);

  const [currentEpIndex, setCurrentEpIndex] = useState(() => {
    if (!episodes.length) return 0;
    if (initialEpisodeId) {
      const idx = episodes.findIndex((ep) => String(ep.Id || ep.id) === String(initialEpisodeId));
      if (idx !== -1) return idx;
    }
    return 0;
  });

  // The episode/season list arrives a tick after mount (the movie detail fetch lands after the
  // episode bootstrap fetch) — once it does, snap to the episode the viewer actually asked for.
  useEffect(() => {
    if (!episodes.length || !initialEpisodeId) return;
    const idx = episodes.findIndex((ep) => String(ep.Id || ep.id) === String(initialEpisodeId));
    if (idx !== -1) setCurrentEpIndex(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episodes.length]);

  const currentEpisodeFromList = episodes[currentEpIndex] || null;
  const currentEpisodeId = currentEpisodeFromList?.Id || currentEpisodeFromList?.id || initialEpisodeId;

  // The season the episodes drawer shows: the viewer's pick, else the one that's playing.
  const [drawerSeason, setDrawerSeason] = useState(null);
  const shownSeason =
    seasons.find((season) => season.number === (drawerSeason ?? currentEpisodeFromList?.seasonNumber)) ??
    seasons[0] ??
    null;

  // Authoritative per-episode data — VideoUrl, the subscription CanWatch gate, and social
  // stats (likes/comments) all live here, refetched fresh every time the episode changes.
  const { data: epDetail, isLoading: epDetailLoading } = useEpisodeDetail(currentEpisodeId);

  const [customVideoSrc, setCustomVideoSrc] = useState(null);
  const [playbackError, setPlaybackError] = useState(null);
  const [isPlayingTrailer, setIsPlayingTrailer] = useState(false);

  // Player state
  const [playing, setPlaying] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [prevVolume, setPrevVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(true);
  const [showControls, setShowControls] = useState(true);

  // UI overlays & menus
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('main'); // 'main' | 'quality' | 'audio' | 'subtitles' | 'speed'
  const [episodesDrawerOpen, setEpisodesDrawerOpen] = useState(false);
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPos, setHoverPos] = useState(0);
  const [centerIcon, setCenterIcon] = useState(null);
  const [showNextEpPrompt, setShowNextEpPrompt] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const promptDismissedRef = useRef(false);
  const [privacyBlur, setPrivacyBlur] = useState(false);

  const switchEpisode = useCallback((newIndex) => {
    setCurrentEpIndex(newIndex);
    setCustomVideoSrc(null);
    setPlaybackError(null);
    setIsPlayingTrailer(false);
    setShowNextEpPrompt(false);
    setPromptDismissed(false);
    promptDismissedRef.current = false;
  }, []);

  // Stream tracks
  const [qualities, setQualities] = useState([]); // [{ id: -1, label: 'Auto' }, ...]
  const [selectedQuality, setSelectedQuality] = useState(-1);
  const [audioTracks, setAudioTracks] = useState([]);
  const [selectedAudio, setSelectedAudio] = useState(0);
  const [subtitles, setSubtitles] = useState([]);
  const [selectedSubtitle, setSelectedSubtitle] = useState(-1); // -1 = Off

  // Determine the actual stream to play. hls.js loads BunnyCDN URLs directly — no proxy needed.
  const actualVideoUrl = customVideoSrc || (isPlayingTrailer ? (title?.trailer || title?.trailerUrl) : epDetail?.VideoUrl) || null;
  const activeVideoSrc = typeof actualVideoUrl === 'string' ? actualVideoUrl : null;

  // Only real episode playback (not a trailer preview or the admin diagnostic stream) earns
  // watch-progress / payout credit.
  const isRealPlayback = !isPlayingTrailer && !customVideoSrc;

  // ── Comments & reactions (this app's own feature — not part of the reference design) ──
  const { data: comments = [] } = useComments(currentEpisodeId);
  const reactMutation = useReactToEpisode();
  const commentMutation = usePostComment();
  const [commentText, setCommentText] = useState('');

  const handleReact = async (reaction) => {
    if (!token) { toast.error('Sign in to react to episodes'); return; }
    try {
      await reactMutation.mutateAsync({
        episodeId: Number(currentEpisodeId),
        reaction: epDetail?.CurrentUserReaction === reaction ? 'None' : reaction,
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save reaction'));
    }
  };

  const handleComment = async () => {
    if (!token) { toast.error('Sign in to comment'); return; }
    if (!commentText.trim()) return;
    try {
      await commentMutation.mutateAsync({ episodeId: Number(currentEpisodeId), commentText: commentText.trim() });
      setCommentText('');
      toast.success('Comment posted!');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to post comment'));
    }
  };

  // ── Payout engine: watch-session heartbeat state ───────────
  // clientSessionId identifies one continuous viewing session (resets per episode load).
  // sessionSecondsRef accumulates ACTIVE watched seconds (not video position) — seeks/jumps
  // don't inflate it, only real forward playback does.
  const lastServerSaveRef = useRef(0);
  const lastKnownTimeRef = useRef(0);
  const lastKnownDurationRef = useRef(0);
  const clientSessionIdRef = useRef(null);
  const sessionSecondsRef = useRef(0);
  const lastPlaybackTimeRef = useRef(0);
  const lastHeartbeatSentRef = useRef(0);
  // Once a heartbeat 401s, the token is bad for the rest of this session — stop hammering the
  // server every 20s with a call we already know will fail. The accumulated seconds are queued
  // instead (see savePendingHeartbeat below) and flushed automatically next time they sign in.
  const tokenInvalidRef = useRef(false);

  const pushProgress = useCallback((force = false) => {
    if (!token || !isRealPlayback || !currentEpisodeId) return;
    const t = lastKnownTimeRef.current;
    const d = lastKnownDurationRef.current;
    if (!t || t < 3) return; // too little watched to be worth recording
    if (!force && t - lastServerSaveRef.current < 15) return;
    lastServerSaveRef.current = t;
    homeApi.updateWatchProgress(currentEpisodeId, Math.floor(t), d ? Math.floor(d) : undefined).catch(() => {});
  }, [currentEpisodeId, token, isRealPlayback]);

  const sendHeartbeat = useCallback((force = false) => {
    if (!token || !isRealPlayback || !clientSessionIdRef.current || tokenInvalidRef.current) return;
    const seconds = Math.floor(sessionSecondsRef.current);
    if (seconds <= 0) return;
    if (!force && seconds === lastHeartbeatSentRef.current) return;
    lastHeartbeatSentRef.current = seconds;
    payoutApi
      .heartbeat(currentEpisodeId, clientSessionIdRef.current, seconds, getDeviceFingerprint())
      .catch((err) => {
        if (err?.response?.status === 401) {
          tokenInvalidRef.current = true;
          savePendingHeartbeat({
            episodeId: currentEpisodeId,
            clientSessionId: clientSessionIdRef.current,
            secondsWatchedTotal: seconds,
            deviceFingerprint: getDeviceFingerprint(),
          });
        }
      });
  }, [currentEpisodeId, token, isRealPlayback]);

  // New watch session per episode — flush whatever the outgoing session accumulated before
  // resetting counters for the new one.
  useEffect(() => {
    if (!isRealPlayback) return undefined;
    clientSessionIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionSecondsRef.current = 0;
    lastPlaybackTimeRef.current = 0;
    lastHeartbeatSentRef.current = 0;
    tokenInvalidRef.current = false;

    return () => { sendHeartbeat(true); pushProgress(true); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEpisodeId, token, isRealPlayback]);

  // Best-effort flush if the tab/window closes mid-playback.
  useEffect(() => {
    const handler = () => { sendHeartbeat(true); pushProgress(true); };
    window.addEventListener('beforeunload', handler);
    window.addEventListener('pagehide', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      window.removeEventListener('pagehide', handler);
    };
  }, [sendHeartbeat, pushProgress]);

  // Anti-screen recording: blur / blank video when window or tab is hidden.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setPrivacyBlur(true);
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
          setPlaying(false);
        }
      } else {
        setPrivacyBlur(false);
      }
    };
    const handleWindowBlur = () => setPrivacyBlur(true);
    const handleWindowFocus = () => setPrivacyBlur(false);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Initialize HLS and Video
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    setQualities([]);
    setSelectedQuality(-1);
    setAudioTracks([]);
    setSelectedAudio(0);
    setSubtitles([]);
    setSelectedSubtitle(-1);

    if (!activeVideoSrc) {
      video.pause();
      return undefined;
    }

    let hls = null;
    const resumeIfNeeded = () => {
      if (!isRealPlayback) return;
      const saved = getProgress(currentEpisodeId);
      if (saved?.progress && saved.progress > 5) video.currentTime = saved.progress;
    };

    if (activeVideoSrc.includes('.m3u8')) {
      if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: false, capLevelToPlayerSize: true });
        hlsRef.current = hls;

        hls.loadSource(activeVideoSrc);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
          const lvls = data.levels.map((lvl, index) => ({
            id: index,
            height: lvl.height,
            bitrate: lvl.bitrate,
            label: lvl.height ? `${lvl.height}p` : `Stream ${index + 1}`,
          }));
          setQualities([{ id: -1, label: 'Auto' }, ...lvls]);

          if (hls.audioTracks && hls.audioTracks.length > 0) {
            setAudioTracks(hls.audioTracks.map((a, i) => ({ id: i, label: a.name || a.lang || `Audio Track ${i + 1}` })));
          }
          if (hls.subtitleTracks && hls.subtitleTracks.length > 0) {
            setSubtitles(hls.subtitleTracks.map((s, i) => ({ id: i, label: s.name || s.lang || `Subtitles ${i + 1}` })));
          }

          resumeIfNeeded();
          video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
          setIsBuffering(false);
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
          setSelectedQuality(hls.autoLevelEnabled ? -1 : data.level);
        });

        hls.on(Hls.Events.BUFFER_STALLED, () => setIsBuffering(true));
        hls.on(Hls.Events.FRAG_BUFFERED, () => setIsBuffering(false));
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (data.fatal) {
            setIsBuffering(false);
            setPlaybackError(data.details || 'The video stream is currently unreachable.');
          }
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = activeVideoSrc;
        video.load();
        resumeIfNeeded();
      }
    } else {
      video.src = activeVideoSrc;
      video.load();
      resumeIfNeeded();
    }

    return () => {
      if (hls) { hls.destroy(); hlsRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVideoSrc]);

  // Lock body scroll while player is active
  useEffect(() => {
    const origOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = origOverflow; };
  }, []);

  // Sync muted and volume
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = muted;
      videoRef.current.volume = volume;
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [muted, volume, playbackSpeed]);

  // Auto-hide controls after 3.5s of inactivity
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (playing && !isDraggingRef.current && !settingsOpen && !episodesDrawerOpen && !commentsDrawerOpen) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3500);
    }
  }, [playing, settingsOpen, episodesDrawerOpen, commentsDrawerOpen]);

  useEffect(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (playing && !settingsOpen && !episodesDrawerOpen && !commentsDrawerOpen) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), 3500);
    }
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [playing, settingsOpen, episodesDrawerOpen, commentsDrawerOpen]);

  const triggerCenterFeedback = (iconName) => {
    setCenterIcon(iconName);
    setTimeout(() => setCenterIcon(null), 600);
  };

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => { setPlaying(true); triggerCenterFeedback('ph:play-fill'); }).catch(() => {});
    } else {
      v.pause();
      setPlaying(false);
      triggerCenterFeedback('ph:pause-fill');
    }
  }, []);

  const skip = useCallback((sec) => {
    const v = videoRef.current;
    if (!v) return;
    const targetDur = v.duration || 0;
    const newTime = Math.max(0, Math.min(targetDur, v.currentTime + sec));
    v.currentTime = newTime;
    setCurrentTime(newTime);
    triggerCenterFeedback(sec > 0 ? 'ph:fast-forward-bold' : 'ph:rewind-bold');
  }, []);

  const toggleMute = useCallback(() => {
    if (muted || volume === 0) {
      const restored = prevVolume > 0 ? prevVolume : 1;
      setVolume(restored);
      setMuted(false);
      if (videoRef.current) { videoRef.current.muted = false; videoRef.current.volume = restored; }
    } else {
      setPrevVolume(volume);
      setMuted(true);
      if (videoRef.current) videoRef.current.muted = true;
    }
  }, [muted, volume, prevVolume]);

  const handleVolumeChange = useCallback((newVol) => {
    setVolume(newVol);
    if (newVol > 0) { setMuted(false); setPrevVolume(newVol); } else { setMuted(true); }
    if (videoRef.current) { videoRef.current.volume = newVol; videoRef.current.muted = newVol === 0; }
  }, []);

  const handleSelectQuality = (qualityId) => {
    setSelectedQuality(qualityId);
    const hls = hlsRef.current;
    if (!hls) return;
    hls.currentLevel = qualityId === -1 ? -1 : qualityId;
  };

  const handleSelectAudio = (trackId) => {
    setSelectedAudio(trackId);
    const hls = hlsRef.current;
    if (hls && hls.audioTracks.length > trackId) hls.audioTrack = trackId;
  };

  const handleSelectSubtitle = (trackId) => {
    setSelectedSubtitle(trackId);
    const hls = hlsRef.current;
    if (hls) hls.subtitleTrack = trackId;
  };

  const handleSelectSpeed = (speed) => {
    setPlaybackSpeed(speed);
    if (videoRef.current) videoRef.current.playbackRate = speed;
  };

  const toggleFullscreen = useCallback(() => {
    const el = playerContainerRef.current;
    if (!el) return;
    const isFull = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement;
    if (isFull) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement));
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' && e.target.type !== 'range') return;
      if (e.target.tagName === 'TEXTAREA') return;

      if (e.key === ' ' || e.key === 'k' || e.key === 'K') {
        e.preventDefault(); togglePlay();
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault(); toggleMute();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault(); toggleFullscreen();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault(); skip(10);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault(); skip(-10);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault(); handleVolumeChange(Math.min(1, volume + 0.1));
      } else if (e.key === 'ArrowDown') {
        e.preventDefault(); handleVolumeChange(Math.max(0, volume - 0.1));
      } else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        if (subtitles.length > 0) handleSelectSubtitle(selectedSubtitle === -1 ? 0 : -1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [togglePlay, toggleMute, toggleFullscreen, skip, handleVolumeChange, volume, subtitles, selectedSubtitle]);

  // Scrubber dragging
  const seekToClientX = useCallback((clientX) => {
    const bar = progressRef.current;
    const v = videoRef.current;
    if (!bar || !v) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const targetDur = duration || v.duration || 0;
    if (targetDur > 0 && isFinite(targetDur)) {
      const newTime = ratio * targetDur;
      v.currentTime = newTime;
      setCurrentTime(newTime);
    }
  }, [duration]);

  const handleProgressMouseDown = (e) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    seekToClientX(e.clientX);

    const onMouseMove = (moveEvent) => seekToClientX(moveEvent.clientX);
    const onMouseUp = (upEvent) => {
      isDraggingRef.current = false;
      seekToClientX(upEvent.clientX);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      resetHideTimer();
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const handleProgressHover = (e) => {
    const bar = progressRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const targetDur = duration || videoRef.current?.duration || 0;
    setHoverPos(ratio * 100);
    setHoverTime(ratio * targetDur);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferProgress = duration > 0 ? (buffered / duration) * 100 : 0;

  // Next episode calculation & countdown progress
  const nextEpisode = episodes[currentEpIndex + 1] || null;
  const nextEpisodeThumb = nextEpisode?.ThumbnailUrl || nextEpisode?.thumbnailUrl || deriveThumbnailFromVideoUrl(nextEpisode?.VideoUrl);
  const remainingSecs = duration > 0 ? Math.max(0, Math.ceil(duration - currentTime)) : 0;
  const promptWindow = Math.min(180, duration > 0 ? duration * 0.5 : 180);
  const countdownProgress = promptWindow > 0
    ? Math.min(100, Math.max(0, ((promptWindow - remainingSecs) / promptWindow) * 100))
    : 0;

  const playNextEpisode = () => {
    if (currentEpIndex < episodes.length - 1) switchEpisode(currentEpIndex + 1);
  };

  const displayEpisode = epDetail || currentEpisodeFromList;
  const noSourceYet = !epDetailLoading && !activeVideoSrc && !playbackError;

  return (
    <div
      ref={playerContainerRef}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onMouseMove={resetHideTimer}
      className="fixed inset-0 z-50 overflow-hidden bg-black text-white select-none"
    >
      {/* ── Privacy / Screen Recording Shield ─────────────────────── */}
      {privacyBlur && (
        <div className="absolute inset-0 z-[100] flex flex-col items-center justify-center bg-black/95 p-6 text-center backdrop-blur-2xl">
          <div className="flex size-16 items-center justify-center rounded-full border border-white/20 bg-white/10">
            <Icon icon="ph:shield-check-bold" className="size-8 text-emerald-400" />
          </div>
          <h2 className="display-sm mt-4 text-2xl text-white">Playback Protected</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Kast content is protected by DRM and copyright policy. Return to this tab to resume playback.
          </p>
        </div>
      )}

      {/* ── Center Buffering Spinner ──────────────────────────────── */}
      {isBuffering && !noSourceYet && !playbackError && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center">
          <div className="flex size-16 items-center justify-center rounded-full bg-black/70 border border-white/15 backdrop-blur-md">
            <Icon icon="ph:spinner-gap-bold" className="size-8 animate-spin text-white" />
          </div>
        </div>
      )}

      {/* ── Center Play/Pause Indicator Feedback ──────────────────── */}
      {centerIcon && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center animate-in zoom-in-50 fade-in duration-200">
          <div className="flex size-20 items-center justify-center rounded-full bg-black/70 border border-white/20 backdrop-blur-md">
            <Icon icon={centerIcon} className="size-9 text-white" />
          </div>
        </div>
      )}

      {/* ── Video Element ───────────────────────────────────────── */}
      <div
        className="relative size-full min-h-screen cursor-pointer"
        onClick={togglePlay}
        onDoubleClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          if (e.clientX - rect.left < rect.width / 2) skip(-10); else skip(10);
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture
          poster={title?.thumbnail}
          className="size-full min-h-screen object-contain"
          onError={() => { setIsBuffering(false); setPlaybackError('Failed to load video stream from origin.'); }}
          onPlay={() => { setPlaying(true); setIsBuffering(false); setPlaybackError(null); }}
          onPause={() => { setPlaying(false); sendHeartbeat(true); pushProgress(true); }}
          onPlaying={() => { setPlaying(true); setIsBuffering(false); }}
          onWaiting={() => setIsBuffering(true)}
          onCanPlay={() => setIsBuffering(false)}
          onTimeUpdate={() => {
            const v = videoRef.current;
            if (!v || isDraggingRef.current) return;
            setCurrentTime(v.currentTime);
            if (v.duration && isFinite(v.duration)) setDuration(v.duration);
            if (v.buffered.length > 0) setBuffered(v.buffered.end(v.buffered.length - 1));

            if (!isRealPlayback) return;

            // Accumulate ACTIVE watched seconds for the payout engine. Only count small forward
            // deltas (normal playback) — seeks/jumps don't inflate qualified minutes.
            const delta = v.currentTime - lastPlaybackTimeRef.current;
            if (delta > 0 && delta < 2) sessionSecondsRef.current += delta;
            lastPlaybackTimeRef.current = v.currentTime;

            lastKnownTimeRef.current = v.currentTime;
            lastKnownDurationRef.current = v.duration || 0;

            if (Math.floor(v.currentTime) % 5 === 0) {
              saveProgress(currentEpisodeId, v.currentTime, v.duration, {
                movieId: title?.id, movieTitle: title?.title, thumbnailUrl: title?.thumbnail, title: displayEpisode?.Title,
              });
            }
            if (token && v.duration && v.currentTime - lastServerSaveRef.current >= 15) {
              lastServerSaveRef.current = v.currentTime;
              homeApi.updateWatchProgress(currentEpisodeId, Math.floor(v.currentTime), Math.floor(v.duration)).catch(() => {});
            }
            if (sessionSecondsRef.current - lastHeartbeatSentRef.current >= 20) sendHeartbeat();

            // Series only: prompt the next episode once we're within the countdown window.
            const remaining = v.duration - v.currentTime;
            const isSeriesWithNextEp = title?.isSeries && currentEpIndex < episodes.length - 1;
            const threshold = Math.min(180, v.duration * 0.5);
            if (isSeriesWithNextEp && remaining > 0 && remaining <= threshold && !promptDismissedRef.current) {
              setShowNextEpPrompt(true);
            }
          }}
          onLoadedMetadata={() => {
            const v = videoRef.current;
            if (v?.duration && isFinite(v.duration)) setDuration(v.duration);
          }}
          onDurationChange={() => {
            const v = videoRef.current;
            if (v?.duration && isFinite(v.duration)) setDuration(v.duration);
          }}
          onEnded={() => {
            setPlaying(false);
            if (isRealPlayback) { sendHeartbeat(true); pushProgress(true); }
            if (title?.isSeries && currentEpIndex < episodes.length - 1) playNextEpisode();
          }}
        >
          <track kind="captions" />
        </video>
      </div>

      {/* ── No Video Stream / Paywall / Sign-in Gate ───────────────── */}
      {noSourceYet && (
        <div
          className="absolute inset-0 z-[55] flex flex-col items-center justify-center bg-black/94 p-6 text-center backdrop-blur-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-5 flex size-20 items-center justify-center rounded-3xl border border-primary/30 bg-primary/10 text-primary shadow-2xl">
            <Icon
              icon={!isAuthenticated ? 'ph:lock-key-bold' : !epDetail?.CanWatch ? 'ph:crown-simple-bold' : 'ph:clock-countdown-bold'}
              className="size-10"
            />
          </div>

          <div className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3.5 py-1 text-xs font-semibold text-white/80 backdrop-blur-md mb-3">
            {!isAuthenticated || !epDetail?.CanWatch ? (
              <Icon icon="ph:shield-check-fill" className="size-3.5 text-emerald-400" />
            ) : (
              <Icon icon="ph:clock-countdown-bold" className="size-3.5 text-amber-400" />
            )}
            {!isAuthenticated ? 'Account Authentication Required' : !epDetail?.CanWatch ? 'Active Membership Required' : 'Coming Soon'}
          </div>

          <h2 className="display-sm text-2xl font-bold sm:text-3xl text-white">
            {!isAuthenticated
              ? `Sign In to Stream "${title?.title}"`
              : !epDetail?.CanWatch
                ? 'Subscribe to Watch Full Title'
                : title?.isSeries
                  ? `Episode ${displayEpisode?.EpisodeNumber ?? currentEpIndex + 1} Is Coming Soon`
                  : `"${title?.title}" Is Coming Soon`}
          </h2>

          <p className="mt-3 max-w-lg text-sm text-white/70 leading-relaxed">
            {!isAuthenticated
              ? `You must be logged in to your Kast account with an active subscription to watch ${title?.isSeries ? `Episode ${displayEpisode?.EpisodeNumber ?? currentEpIndex + 1}` : 'the full movie'}.`
              : !epDetail?.CanWatch
                ? `An active subscription is required to stream this ${title?.isSeries ? 'series episode' : 'movie'}. Subscribe to unlock unlimited streaming.`
                : `We are still getting ${title?.isSeries ? 'this episode' : title?.title} ready to stream. Watch the official trailer now, and check back shortly for the full ${title?.isSeries ? 'episode' : 'movie'}.`}
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {!isAuthenticated ? (
              <Button asChild className="h-11 rounded-full bg-white px-7 text-sm font-semibold text-black shadow-lg hover:bg-white/90">
                <Link href={`/login?redirect=/watch/${initialEpisodeId}`}>
                  <Icon icon="ph:sign-in-bold" className="mr-1.5 size-4" />
                  Sign In to Watch
                </Link>
              </Button>
            ) : !epDetail?.CanWatch ? (
              <Button asChild className="h-11 rounded-full bg-primary px-7 text-sm font-semibold text-primary-foreground shadow-lg hover:bg-primary/90">
                <Link href="/subscribe">
                  <Icon icon="ph:crown-simple-bold" className="mr-1.5 size-4" />
                  Subscribe Now
                </Link>
              </Button>
            ) : null}

            {(title?.trailer || title?.trailerUrl) && (
              <button
                type="button"
                onClick={() => { setIsPlayingTrailer(true); setPlaybackError(null); }}
                className="flex h-11 items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/15 px-6 text-sm font-semibold text-amber-300 backdrop-blur-md transition-colors hover:bg-amber-500/25"
              >
                <Icon icon="ph:film-reel-bold" className="size-4" />
                Watch Trailer Instead
              </button>
            )}

            {isAdmin && (
              <button
                type="button"
                onClick={() => { setCustomVideoSrc('https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8'); setPlaybackError(null); }}
                className="flex h-11 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 text-sm font-medium text-white hover:bg-white/20"
              >
                <Icon icon="ph:play-circle-bold" className="size-4" />
                Play Multi-Track Test
              </button>
            )}

            <Button asChild variant="ghost" className="h-11 rounded-full border border-white/10 px-5 text-sm font-medium text-white/70 hover:bg-white/10 hover:text-white">
              <Link href={`/movie/${title?.id}`}>
                <Icon icon="ph:arrow-left" className="mr-1.5 size-4" />
                Back to Details
              </Link>
            </Button>
          </div>
        </div>
      )}

      {/* ── Playback Error Overlay ─────────────────────────────────── */}
      {playbackError && (
        <div className="absolute inset-0 z-[55] flex flex-col items-center justify-center bg-black/92 p-6 text-center backdrop-blur-md" onClick={(e) => e.stopPropagation()}>
          <div className="mb-4 flex size-16 items-center justify-center rounded-2xl border border-destructive/30 bg-destructive/20 text-destructive shadow-2xl">
            <Icon icon="ph:warning-circle-bold" className="size-8" />
          </div>
          <h2 className="display-sm text-xl font-bold sm:text-2xl">Stream Temporarily Unavailable</h2>
          <p className="mt-2 max-w-md text-sm text-muted-foreground leading-relaxed">
            {isAdmin
              ? 'The origin stream CDN is offline or suspended. You can launch our multi-track HD cinema test stream to preview all player features.'
              : 'This stream could not be loaded. Try again in a moment, or head back to the title for the trailer and episode list.'}
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            {isAdmin && (
              <button
                type="button"
                onClick={() => { setPlaybackError(null); setIsBuffering(true); setCustomVideoSrc('https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8'); }}
                className="flex h-11 items-center gap-2 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:scale-102 hover:bg-primary/90"
              >
                <Icon icon="ph:play-fill" className="size-4" />
                Play Multi-Track Demo
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setPlaybackError(null);
                setIsBuffering(true);
                if (hlsRef.current) { hlsRef.current.loadSource(activeVideoSrc); hlsRef.current.startLoad(); }
                else videoRef.current?.load();
              }}
              className="flex h-11 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 text-sm font-medium hover:bg-white/20"
            >
              <Icon icon="ph:arrow-clockwise" className="size-4" />
              Retry
            </button>
            <Link href={`/movie/${title?.id || ''}`} className="flex h-11 items-center gap-2 rounded-full border border-white/10 px-5 text-sm font-medium text-muted-foreground hover:bg-white/10 hover:text-white">
              <Icon icon="ph:arrow-left" className="size-4" />
              Back to Details
            </Link>
          </div>
        </div>
      )}

      {/* ── Next Episode Prompt Banner ──────── */}
      {showNextEpPrompt && nextEpisode && !promptDismissed && (
        <div
          className="absolute bottom-28 right-6 z-40 flex w-80 sm:w-96 flex-col gap-3 rounded-2xl border border-white/20 bg-black/90 p-4 shadow-2xl backdrop-blur-2xl animate-in slide-in-from-right-6 duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-2.5">
            <div className="flex items-center gap-2">
              <span className="relative flex size-2.5">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex size-2.5 rounded-full bg-primary" />
              </span>
              <span className="text-[11px] font-bold tracking-wider text-white/80 uppercase">
                Up Next in {formatTime(remainingSecs)}
              </span>
            </div>
            <button
              type="button"
              onClick={() => { setPromptDismissed(true); promptDismissedRef.current = true; }}
              aria-label="Dismiss up next prompt"
              className="flex size-6 items-center justify-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            >
              <Icon icon="ph:x-bold" className="size-3.5" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative aspect-video w-28 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-card">
              {nextEpisodeThumb ? (
                <Image src={nextEpisodeThumb} alt={nextEpisode.Title || ''} fill sizes="112px" className="object-cover" />
              ) : (
                <div className="flex size-full items-center justify-center bg-white/5 text-muted-foreground">
                  <Icon icon="ph:film-slate" className="size-6" />
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="flex size-7 items-center justify-center rounded-full bg-white/90 text-black shadow-md">
                  <Icon icon="ph:play-fill" className="ml-0.5 size-3.5" />
                </div>
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-[11px] font-semibold text-white/60">
                <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold text-white/80">
                  E{(nextEpisode.episodeNumber ?? currentEpIndex + 2)}
                </span>
                {formatEpisodeDuration(nextEpisode) && <span>{formatEpisodeDuration(nextEpisode)}</span>}
              </div>
              <p className="mt-1 truncate text-sm font-bold text-white leading-tight">
                {nextEpisode.Title || nextEpisode.title || `Episode ${nextEpisode.episodeNumber}`}
              </p>
              {(nextEpisode.Description || nextEpisode.description) && (
                <p className="mt-1 line-clamp-1 text-xs text-white/60">{nextEpisode.Description || nextEpisode.description}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1.5 pt-1">
            <div className="flex items-center justify-between text-[11px] text-white/70">
              <span className="flex items-center gap-1.5">
                <Icon icon="ph:clock-countdown-bold" className="size-3.5 text-primary" />
                Autoplay countdown
              </span>
              <span className="font-mono text-xs font-semibold text-white">{formatTime(remainingSecs)}</span>
            </div>
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/15">
              <div className="h-full brand-sweep transition-all duration-300 rounded-full" style={{ width: `${countdownProgress}%` }} />
            </div>
          </div>

          <div className="mt-1 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={() => { setPromptDismissed(true); promptDismissedRef.current = true; }}
              className="rounded-full px-3.5 py-1.5 text-xs font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
            >
              Dismiss
            </button>
            <Button type="button" onClick={playNextEpisode} className="h-9 rounded-full bg-white px-5 text-xs font-bold text-black shadow-lg hover:bg-white/90 hover:scale-102 transition-transform">
              <Icon icon="ph:play-fill" className="mr-1.5 size-3.5" />
              Play Now
            </Button>
          </div>
        </div>
      )}

      {/* ── Top Header Controls Bar ───────────────────────────────── */}
      <div
        className={cn(
          'absolute inset-x-0 top-0 z-50 flex items-center justify-between px-6 pt-5 pb-12 bg-gradient-to-b from-black/90 via-black/50 to-transparent transition-opacity duration-300',
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-4">
          <Link
            href={`/movie/${title?.id}`}
            aria-label="Back to movie details"
            className="flex size-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md transition-colors hover:border-white/30 hover:bg-white/20"
          >
            <Icon icon="ph:arrow-left-bold" className="size-5" />
          </Link>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold tracking-wider text-white/60 uppercase">
                {title?.isSeries
                  ? currentEpisodeFromList
                    ? `Series · S${currentEpisodeFromList.seasonNumber} · E${currentEpisodeFromList.episodeNumber}`
                    : 'Series'
                  : title?.category || 'Movie'}
              </span>
              <span className="h-2.5 w-px bg-white/20" />
              <div className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400">
                <Icon icon="ph:shield-check-fill" className="size-3.5" />
                DRM Protected
              </div>
              {isPlayingTrailer && (
                <>
                  <span className="h-2.5 w-px bg-white/20" />
                  <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/25 px-2.5 py-0.5 text-[11px] font-bold text-amber-300">
                    <Icon icon="ph:film-reel-bold" className="size-3" />
                    TRAILER PREVIEW
                  </div>
                  <button type="button" onClick={() => setIsPlayingTrailer(false)} className="text-xs text-white/60 underline hover:text-white">
                    Exit Preview
                  </button>
                </>
              )}
            </div>
            <h1 className="display-sm mt-0.5 text-lg sm:text-xl text-white font-semibold leading-tight">
              {title?.isSeries && displayEpisode?.Title ? `${title.title}: ${displayEpisode.Title}` : title?.title}
            </h1>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-white/90 backdrop-blur-md">
          <Icon icon="ph:broadcast-bold" className="size-3.5 text-violet" />
          <span>{selectedQuality === -1 ? 'Auto (Adaptive)' : qualities.find((q) => q.id === selectedQuality)?.label || 'HD'}</span>
        </div>
      </div>

      {/* ── Settings Drawer / Popover ─────────────────────────────── */}
      {settingsOpen && (
        <div
          className="absolute right-6 bottom-24 z-50 w-72 overflow-hidden rounded-2xl border border-white/15 bg-black/90 p-4 shadow-2xl backdrop-blur-2xl animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {settingsTab === 'main' && (
            <div className="flex flex-col gap-1 text-sm">
              <div className="border-b border-white/10 pb-2.5 mb-1 flex items-center justify-between">
                <span className="font-semibold text-white">Playback Settings</span>
                <button type="button" onClick={() => setSettingsOpen(false)} className="text-white/60 hover:text-white">
                  <Icon icon="ph:x-bold" className="size-4" />
                </button>
              </div>

              <button type="button" onClick={() => setSettingsTab('quality')} className="flex items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-white/10 text-white/90">
                <div className="flex items-center gap-2.5">
                  <Icon icon="ph:monitor-bold" className="size-4 text-violet" />
                  <span>Quality</span>
                </div>
                <span className="text-xs text-white/50">
                  {selectedQuality === -1 ? 'Auto' : qualities.find((q) => q.id === selectedQuality)?.label || 'Auto'}
                  {' ›'}
                </span>
              </button>

              <button type="button" onClick={() => setSettingsTab('audio')} className="flex items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-white/10 text-white/90">
                <div className="flex items-center gap-2.5">
                  <Icon icon="ph:speaker-high-bold" className="size-4 text-violet" />
                  <span>Audio Track</span>
                </div>
                <span className="text-xs text-white/50">{audioTracks[selectedAudio]?.label || 'Stereo'}{' ›'}</span>
              </button>

              <button type="button" onClick={() => setSettingsTab('subtitles')} className="flex items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-white/10 text-white/90">
                <div className="flex items-center gap-2.5">
                  <Icon icon="ph:subtitles-bold" className="size-4 text-violet" />
                  <span>Subtitles</span>
                </div>
                <span className="text-xs text-white/50">{selectedSubtitle === -1 ? 'Off' : subtitles[selectedSubtitle]?.label || 'On'}{' ›'}</span>
              </button>

              <button type="button" onClick={() => setSettingsTab('speed')} className="flex items-center justify-between rounded-xl px-3 py-2 text-left transition-colors hover:bg-white/10 text-white/90">
                <div className="flex items-center gap-2.5">
                  <Icon icon="ph:gauge-bold" className="size-4 text-violet" />
                  <span>Speed</span>
                </div>
                <span className="text-xs text-white/50">{playbackSpeed === 1 ? 'Normal' : `${playbackSpeed}x`}{' ›'}</span>
              </button>
            </div>
          )}

          {settingsTab === 'quality' && (
            <div>
              <div className="border-b border-white/10 pb-2.5 mb-2 flex items-center gap-2">
                <button type="button" onClick={() => setSettingsTab('main')} className="text-white/60 hover:text-white">
                  <Icon icon="ph:arrow-left-bold" className="size-4" />
                </button>
                <span className="text-sm font-semibold text-white">Video Quality</span>
              </div>
              <div className="flex flex-col gap-1 max-h-60 overflow-y-auto no-scrollbar">
                {qualities.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => { handleSelectQuality(q.id); setSettingsTab('main'); }}
                    className={cn('flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors', selectedQuality === q.id ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10')}
                  >
                    <span>{q.label}</span>
                    {selectedQuality === q.id && <Icon icon="ph:check-bold" className="size-4 text-violet" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {settingsTab === 'audio' && (
            <div>
              <div className="border-b border-white/10 pb-2.5 mb-2 flex items-center gap-2">
                <button type="button" onClick={() => setSettingsTab('main')} className="text-white/60 hover:text-white">
                  <Icon icon="ph:arrow-left-bold" className="size-4" />
                </button>
                <span className="text-sm font-semibold text-white">Audio Tracks</span>
              </div>
              <div className="flex flex-col gap-1">
                {audioTracks.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-white/50">Original Stereo Audio</div>
                ) : (
                  audioTracks.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => { handleSelectAudio(a.id); setSettingsTab('main'); }}
                      className={cn('flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors', selectedAudio === a.id ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10')}
                    >
                      <span>{a.label}</span>
                      {selectedAudio === a.id && <Icon icon="ph:check-bold" className="size-4 text-violet" />}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {settingsTab === 'subtitles' && (
            <div>
              <div className="border-b border-white/10 pb-2.5 mb-2 flex items-center gap-2">
                <button type="button" onClick={() => setSettingsTab('main')} className="text-white/60 hover:text-white">
                  <Icon icon="ph:arrow-left-bold" className="size-4" />
                </button>
                <span className="text-sm font-semibold text-white">Subtitles</span>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  onClick={() => { handleSelectSubtitle(-1); setSettingsTab('main'); }}
                  className={cn('flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors', selectedSubtitle === -1 ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10')}
                >
                  <span>Off</span>
                  {selectedSubtitle === -1 && <Icon icon="ph:check-bold" className="size-4 text-violet" />}
                </button>
                {subtitles.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => { handleSelectSubtitle(s.id); setSettingsTab('main'); }}
                    className={cn('flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors', selectedSubtitle === s.id ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10')}
                  >
                    <span>{s.label}</span>
                    {selectedSubtitle === s.id && <Icon icon="ph:check-bold" className="size-4 text-violet" />}
                  </button>
                ))}
              </div>
            </div>
          )}

          {settingsTab === 'speed' && (
            <div>
              <div className="border-b border-white/10 pb-2.5 mb-2 flex items-center gap-2">
                <button type="button" onClick={() => setSettingsTab('main')} className="text-white/60 hover:text-white">
                  <Icon icon="ph:arrow-left-bold" className="size-4" />
                </button>
                <span className="text-sm font-semibold text-white">Playback Speed</span>
              </div>
              <div className="flex flex-col gap-1">
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => { handleSelectSpeed(s); setSettingsTab('main'); }}
                    className={cn('flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors', playbackSpeed === s ? 'bg-white/15 font-semibold text-white' : 'text-white/80 hover:bg-white/10')}
                  >
                    <span>{s === 1 ? '1x (Normal)' : `${s}x`}</span>
                    {playbackSpeed === s && <Icon icon="ph:check-bold" className="size-4 text-violet" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Episodes Slide-Out Drawer (Series Only) ───────────────── */}
      {episodesDrawerOpen && title?.isSeries && (
        <div
          className="absolute right-0 inset-y-0 z-50 w-full sm:w-96 border-l border-white/15 bg-black/95 p-6 shadow-2xl backdrop-blur-2xl animate-in slide-in-from-right duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div>
              <h2 className="text-base font-semibold text-white">Episodes</h2>
              <p className="text-xs text-muted-foreground">{title.title}</p>
            </div>
            <button type="button" onClick={() => setEpisodesDrawerOpen(false)} className="flex size-8 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white">
              <Icon icon="ph:x-bold" className="size-4" />
            </button>
          </div>

          {seasons.length > 1 && shownSeason ? (
            <div role="group" aria-label="Choose a season" className="no-scrollbar -mx-1 mt-4 flex gap-1.5 overflow-x-auto px-1 py-1">
              {seasons.map((season) => {
                const active = season.number === shownSeason.number;
                const playingHere = season.number === currentEpisodeFromList?.seasonNumber;
                return (
                  <button
                    key={season.number}
                    type="button"
                    onClick={() => setDrawerSeason(season.number)}
                    aria-pressed={active}
                    className={cn(
                      'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none',
                      active ? 'bg-white text-black' : 'bg-white/10 text-white/75 hover:bg-white/20 hover:text-white'
                    )}
                  >
                    {season.label}
                    {playingHere ? <span className={cn('size-1.5 rounded-full', active ? 'bg-black' : 'bg-violet')} aria-label="Now playing" /> : null}
                  </button>
                );
              })}
            </div>
          ) : null}

          <div className="mt-4 flex flex-col gap-3 max-h-[76vh] overflow-y-auto no-scrollbar pr-1">
            {(shownSeason?.episodes ?? []).map((ep) => {
              const index = episodes.indexOf(ep);
              const isCurrent = currentEpIndex === index;
              const epDuration = formatEpisodeDuration(ep);
              const epThumb = ep.ThumbnailUrl || deriveThumbnailFromVideoUrl(ep.VideoUrl);
              return (
                <button
                  key={ep.Id || ep.id || `${ep.seasonNumber}-${ep.episodeNumber}`}
                  type="button"
                  onClick={() => { switchEpisode(index); setEpisodesDrawerOpen(false); }}
                  aria-current={isCurrent || undefined}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-all cursor-pointer focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:outline-none',
                    isCurrent ? 'border border-violet/40 bg-violet/15 ring-1 ring-violet/50' : 'border border-white/8 bg-white/5 hover:border-white/20 hover:bg-white/10'
                  )}
                >
                  <div className="relative aspect-video w-24 shrink-0 overflow-hidden rounded-lg bg-card">
                    {epThumb ? (
                      <Image src={epThumb} alt="" fill sizes="96px" className="object-cover" />
                    ) : (
                      <div className="flex size-full items-center justify-center bg-white/5 text-muted-foreground">
                        <Icon icon="ph:film-slate" className="size-5" />
                      </div>
                    )}
                    {isCurrent && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <Icon icon="ph:speaker-simple-high-fill" className="size-5 text-white animate-pulse" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold text-white/50">S{ep.seasonNumber} · E{ep.episodeNumber}</div>
                    <div className="truncate text-sm font-semibold text-white">{ep.Title || `Episode ${ep.episodeNumber}`}</div>
                    {epDuration && <div className="text-xs text-white/40">{epDuration}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Comments Slide-Out Drawer ───────────────────────────────── */}
      {commentsDrawerOpen && (
        <div
          className="absolute right-0 inset-y-0 z-50 flex w-full sm:w-96 flex-col border-l border-white/15 bg-black/95 p-6 shadow-2xl backdrop-blur-2xl animate-in slide-in-from-right duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-white/10 pb-4">
            <div>
              <h2 className="text-base font-semibold text-white">Comments</h2>
              <p className="text-xs text-muted-foreground">{epDetail?.CommentCount ?? comments.length} comments</p>
            </div>
            <button type="button" onClick={() => setCommentsDrawerOpen(false)} className="flex size-8 items-center justify-center rounded-full text-white/70 hover:bg-white/10 hover:text-white">
              <Icon icon="ph:x-bold" className="size-4" />
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleReact('Like')}
              className={cn('flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-all', epDetail?.CurrentUserReaction === 'Like' ? 'border-primary bg-primary/20 text-white' : 'border-white/15 bg-white/5 text-white/70 hover:border-white/30 hover:text-white')}
            >
              <Icon icon="ph:thumbs-up-fill" className="size-4" />
              <span>{epDetail?.LikeCount || 0}</span>
            </button>
            <button
              type="button"
              onClick={() => handleReact('DoubleLike')}
              className={cn('flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-all', epDetail?.CurrentUserReaction === 'DoubleLike' ? 'border-primary bg-primary/20 text-white' : 'border-white/15 bg-white/5 text-white/70 hover:border-white/30 hover:text-white')}
            >
              <Icon icon="ph:heart-fill" className="size-4" />
              <span>{epDetail?.DoubleLikeCount || 0}</span>
            </button>
            <button
              type="button"
              onClick={() => handleReact('Dislike')}
              className={cn('flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-all', epDetail?.CurrentUserReaction === 'Dislike' ? 'border-destructive bg-destructive/20 text-white' : 'border-white/15 bg-white/5 text-white/70 hover:border-white/30 hover:text-white')}
            >
              <Icon icon="ph:thumbs-down-fill" className="size-4" />
              <span>{epDetail?.DislikeCount || 0}</span>
            </button>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <textarea
              placeholder={token ? 'Add to the discussion…' : 'Sign in to leave a comment'}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              rows={2}
              disabled={!token}
              className="w-full resize-none rounded-xl border border-white/12 bg-white/5 p-3 text-sm text-white placeholder:text-white/40 focus:border-white/30 focus:bg-white/10 focus:outline-none transition"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleComment(); }}
            />
            <Button
              onClick={handleComment}
              disabled={!token || !commentText.trim() || commentMutation.isPending}
              size="sm"
              className="self-end rounded-full px-4 text-xs font-semibold"
            >
              <Icon icon="ph:paper-plane-right-fill" className="mr-1.5 size-3.5" />
              Post
            </Button>
          </div>

          <div className="mt-4 flex-1 space-y-3 overflow-y-auto no-scrollbar pr-1">
            {comments.map((c) => (
              <div key={c.Id || c.id} className="flex gap-3 rounded-xl border border-white/6 bg-white/[0.02] p-3">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary font-semibold text-xs ring-1 ring-white/10">
                  {c.CommenterPhoto ? (
                    <img src={c.CommenterPhoto} alt={c.CommenterName} className="size-full rounded-full object-cover" />
                  ) : (
                    (c.CommenterName || 'U').charAt(0).toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-white">{c.CommenterName || 'Anonymous'}</span>
                    {c.DatePosted && <span className="text-[10px] text-white/40">{dayjs(c.DatePosted).fromNow()}</span>}
                  </div>
                  <p className="mt-1 text-xs text-white/80 leading-relaxed break-words">{c.CommentText || c.Text}</p>
                </div>
              </div>
            ))}
            {comments.length === 0 && (
              <p className="py-8 text-center text-xs text-white/40">No comments yet. Be the first to share your thoughts!</p>
            )}
          </div>
        </div>
      )}

      {/* ── Bottom Controls Bar ───────────────────────────────────── */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 z-40 bg-gradient-to-t from-black/95 via-black/70 to-transparent pt-14 pb-5 px-6 transition-opacity duration-300',
          showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          ref={progressRef}
          className="group/progress relative h-2 cursor-pointer rounded-full bg-white/20 transition-all hover:h-3"
          onMouseDown={handleProgressMouseDown}
          onMouseMove={handleProgressHover}
          onMouseLeave={() => setHoverTime(null)}
        >
          <div className="absolute inset-y-0 left-0 rounded-full bg-white/25" style={{ width: `${bufferProgress}%` }} />
          <div className="absolute inset-y-0 left-0 rounded-full brand-sweep" style={{ width: `${progress}%` }} />
          <div
            className="absolute top-1/2 -translate-y-1/2 size-4 -ml-2 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.9)] opacity-0 transition-opacity group-hover/progress:opacity-100"
            style={{ left: `${progress}%` }}
          />
          {hoverTime !== null && (
            <div
              className="pointer-events-none absolute -top-9 -translate-x-1/2 rounded-md bg-black/90 px-2.5 py-1 text-xs font-semibold text-white shadow-xl border border-white/15"
              style={{ left: `${hoverPos}%` }}
            >
              {formatTime(hoverTime)}
            </div>
          )}
        </div>

        <div className="mt-3.5 flex items-center gap-2">
          <button type="button" onClick={togglePlay} className="flex size-10 items-center justify-center rounded-full text-white/90 transition-colors hover:bg-white/15 hover:text-white" aria-label={playing ? 'Pause' : 'Play'}>
            <Icon icon={playing ? 'ph:pause-fill' : 'ph:play-fill'} className="size-6" />
          </button>

          <button type="button" onClick={() => skip(-10)} className="flex size-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/15 hover:text-white" aria-label="Rewind 10 seconds">
            <Icon icon="ph:rewind-bold" className="size-5" />
          </button>

          <button type="button" onClick={() => skip(10)} className="flex size-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/15 hover:text-white" aria-label="Forward 10 seconds">
            <Icon icon="ph:fast-forward-bold" className="size-5" />
          </button>

          {title?.isSeries && currentEpIndex < episodes.length - 1 && (
            <button type="button" onClick={playNextEpisode} className="hidden sm:flex size-9 items-center justify-center rounded-full text-white/75 transition-colors hover:bg-white/15 hover:text-white" title="Next episode" aria-label="Next episode">
              <Icon icon="ph:skip-forward-bold" className="size-5" />
            </button>
          )}

          <div className="group/vol relative flex items-center gap-1.5 ml-1">
            <button type="button" onClick={toggleMute} className="flex size-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white" aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'}>
              <Icon icon={muted || volume === 0 ? 'ph:speaker-simple-x-bold' : volume < 0.5 ? 'ph:speaker-simple-low-bold' : 'ph:speaker-simple-high-bold'} className="size-5" />
            </button>
            <div className="w-0 overflow-hidden transition-all duration-200 group-hover/vol:w-20 group-focus-within/vol:w-20">
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                className="h-1.5 w-20 cursor-pointer accent-white"
                aria-label="Volume slider"
              />
            </div>
          </div>

          <span className="ml-2 text-xs sm:text-sm font-medium tabular-nums text-white/75">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>

          <div className="flex-1" />

          {title?.isSeries && episodes.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setEpisodesDrawerOpen(!episodesDrawerOpen);
                setSettingsOpen(false);
                setCommentsDrawerOpen(false);
                setDrawerSeason(null);
              }}
              className={cn('flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium backdrop-blur-md transition-colors', episodesDrawerOpen ? 'bg-white text-black' : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white')}
              title="View all episodes"
            >
              <Icon icon="ph:squares-four-bold" className="size-4" />
              <span className="hidden sm:inline">Episodes</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              setCommentsDrawerOpen(!commentsDrawerOpen);
              setSettingsOpen(false);
              setEpisodesDrawerOpen(false);
            }}
            className={cn('flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium backdrop-blur-md transition-colors', commentsDrawerOpen ? 'bg-white text-black' : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white')}
            title="Comments"
          >
            <Icon icon="ph:chat-circle-text-bold" className="size-4" />
            {(epDetail?.CommentCount ?? 0) > 0 && <span className="hidden sm:inline">{epDetail.CommentCount}</span>}
          </button>

          {subtitles.length > 0 && (
            <button
              type="button"
              onClick={() => handleSelectSubtitle(selectedSubtitle === -1 ? 0 : -1)}
              className={cn('flex size-9 items-center justify-center rounded-full transition-colors', selectedSubtitle !== -1 ? 'bg-white/20 text-white ring-1 ring-white/30' : 'text-white/75 hover:bg-white/15 hover:text-white')}
              title={selectedSubtitle !== -1 ? 'Disable subtitles' : 'Enable subtitles'}
              aria-label="Subtitles"
            >
              <Icon icon="ph:subtitles-bold" className="size-5" />
            </button>
          )}

          <button
            type="button"
            onClick={() => { setSettingsOpen(!settingsOpen); setSettingsTab('main'); setEpisodesDrawerOpen(false); setCommentsDrawerOpen(false); }}
            className={cn('flex size-9 items-center justify-center rounded-full transition-colors', settingsOpen ? 'bg-white text-black' : 'text-white/75 hover:bg-white/15 hover:text-white')}
            title="Playback settings"
            aria-label="Playback settings"
          >
            <Icon icon="ph:gear-bold" className="size-5" />
          </button>

          <button type="button" onClick={toggleFullscreen} className="flex size-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/15 hover:text-white" aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
            <Icon icon={isFullscreen ? 'ph:corners-in-bold' : 'ph:corners-out-bold'} className="size-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CinemaPlayer;
